import { useState, useRef, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabaseService } from '../../lib/supabase-service';
import { supabase } from '../../lib/supabase';
import { 
  X, Save, Upload, Plus, Calendar as CalendarIcon, MapPin, ChevronLeft, ChevronRight, Clock,
  Hammer, Palette, Coffee, Store, Gamepad2, Trophy, Film, Mountain, Home, UtensilsCrossed, GraduationCap, Plane, TreePine, Sparkles,
  DollarSign, Globe, Phone, Users, AlertCircle, Menu, User
} from 'lucide-react';
import { format } from 'date-fns';

export default function EventForm({ event = null, onClose, mode = 'add' }) {
  const { addFamilyActivity, updateFamilyActivity, addActivityToCalendar, currentUser, family } = useFamilyContext();
  const fileInputRef = useRef(null);
  const titleInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  
  // Helper function to properly format dates for inputs
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // If it's already in YYYY-MM-DD format, return as is
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    
    // If it's an ISO string or has time, extract just the date part
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    
    // Try to parse and format the date, keeping it in local timezone
    try {
      const date = new Date(dateString + 'T00:00:00'); // Force local time
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.warn('Error parsing date:', dateString, error);
      return '';
    }
  };

  const [formData, setFormData] = useState({
    title: event?.title || '',
    description: event?.description || '',
    category: event?.category || 'other',
    location: event?.location || '',
    startDate: formatDateForInput(event?.start_date || event?.date), // Support legacy date field
    endDate: formatDateForInput(event?.end_date),
    time: event?.time && event.time !== 'TBD' ? event.time : '', // Don't default time if not set
    image: event?.image || null,
    potential_dates: event?.potential_dates || [],
    confirmedForCalendar: event?.confirmed_for_calendar || false,
    colorTheme: event?.color_theme || 'gold', // Default to gold
    // Enhanced details
    priceRange: event?.pricing?.range || '',
    priceNotes: event?.pricing?.notes || '',
    website: event?.website || '',
    phone: event?.phone || '',
    additionalImages: event?.additional_images || [],
    estimatedDuration: event?.estimated_duration || '',
    ageRange: event?.age_range || '',
    specialRequirements: event?.special_requirements || '',
    menuHighlights: event?.menu_highlights || [],
    // RSVP functionality
    rsvpEnabled: event?.rsvp_enabled || false,
    rsvpDeadline: event?.rsvp_deadline ? formatDateForInput(event.rsvp_deadline) : '',
    // Creator
    created_by: event?.created_by || currentUser?.id
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showExpandedCategories, setShowExpandedCategories] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [titleFocused, setTitleFocused] = useState(false);
  const [descriptionFocused, setDescriptionFocused] = useState(false);
  const [schedulingMode, setSchedulingMode] = useState(formData.startDate ? 'date' : null); // null, 'weekend', 'someday', 'date'
  const [isMultiDay, setIsMultiDay] = useState(!!formData.endDate);

  // Get all family members for creator selection
  const allFamilyMembers = [
    ...(family?.parents || []),
    ...(family?.kids || [])
  ];

  // Focus title input when modal opens
  useEffect(() => {
    if (titleInputRef.current) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, []);

  // Auto-save functionality
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.title.trim() || formData.description.trim()) {
        // Save draft to localStorage
        localStorage.setItem('familyEventDraft', JSON.stringify(formData));
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [formData]);

  const categories = [
    { value: 'woodworking', label: 'Woodworking', icon: <Hammer className="w-4 h-4" /> },
    { value: 'art-craft', label: 'Art & Craft', icon: <Palette className="w-4 h-4" /> },
    { value: 'casual-bites', label: 'Casual Bites', icon: <Coffee className="w-4 h-4" /> },
    { value: 'specialty-shops', label: 'Specialty Shops', icon: <Store className="w-4 h-4" /> },
    { value: 'games-interactive', label: 'Games & Interactive Fun', icon: <Gamepad2 className="w-4 h-4" /> },
    { value: 'sports-shows', label: 'Sports & Live Shows', icon: <Trophy className="w-4 h-4" /> },
    { value: 'movies-music', label: 'Movies & Music', icon: <Film className="w-4 h-4" /> },
    { value: 'outdoor-adventures', label: 'Outdoor Adventures', icon: <Mountain className="w-4 h-4" /> },
    { value: 'at-home-fun', label: 'At-Home Fun', icon: <Home className="w-4 h-4" /> },
    { value: 'dining-out', label: 'Dining Out', icon: <UtensilsCrossed className="w-4 h-4" /> },
    { value: 'educational', label: 'Educational Experiences', icon: <GraduationCap className="w-4 h-4" /> },
    { value: 'travel-trips', label: 'Travel & Day Trips', icon: <Plane className="w-4 h-4" /> },
    { value: 'festivals-events', label: 'Festivals & Events', icon: <TreePine className="w-4 h-4" /> },
    { value: 'other', label: 'Other', icon: <Sparkles className="w-4 h-4" /> }
  ];

  // Color theme options for activities without images
  const colorThemes = [
    { value: 'magenta', label: 'Magenta', color: '#e91e63', gradient: 'gradient-magenta', description: 'Communication & messaging' },
    { value: 'coral', label: 'Coral', color: '#ff6b6b', gradient: 'gradient-coral', description: 'Urgent alerts & warnings' },
    { value: 'deep-orange', label: 'Deep Orange', color: '#ff7043', gradient: 'gradient-deep-orange', description: 'Energy & enthusiasm' },
    { value: 'gold', label: 'Gold', color: '#ffd54f', gradient: 'gradient-gold', description: 'Success & achievements' },
    { value: 'teal', label: 'Teal', color: '#26a69a', gradient: 'gradient-teal', description: 'Primary actions & CTAs' },
    { value: 'azure', label: 'azure', color: '#42a5f5', gradient: 'gradient-azure', description: 'Information & trust' },
    { value: 'indigo', label: 'Indigo', color: '#7e57c2', gradient: 'gradient-indigo', description: 'Focus & concentration' },
    { value: 'violet', label: 'Violet', color: '#ab47bc', gradient: 'gradient-violet', description: 'Creativity & imagination' }
  ];

  // Get recent categories (mock data for now - in real app, this would come from user's activity history)
  const getRecentCategories = () => {
    const recent = localStorage.getItem('recentCategories');
    if (recent) {
      try {
        const parsed = JSON.parse(recent);
        return categories.filter(cat => parsed.includes(cat.value)).slice(0, 3);
      } catch {
        // Fallback to most common categories
        return categories.slice(0, 3);
      }
    }
    return categories.slice(0, 3);
  };

  const recentCategories = getRecentCategories();

  // Location quick options
  const quickLocations = ['Raleigh', 'Cary', 'Apex', 'Holly Springs', 'Fuquay Varina'];

  // Calendar utilities
  const formatCalendarDate = (date) => {
    return format(date, 'yyyy-MM-dd');
  };

  const navigateCalendar = (direction) => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCalendarDate(newDate);
  };

  const selectCalendarDate = (day) => {
    const selectedDate = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day);
    const formattedDate = formatCalendarDate(selectedDate);
    setFormData(prev => ({ 
      ...prev, 
      startDate: formattedDate,
      // If it's multi-day and no end date set, set end date to same day
      endDate: isMultiDay && !prev.endDate ? formattedDate : prev.endDate
    }));
    setSchedulingMode('date');
    setShowCalendar(false);
  };

  // Handle Enter key navigation
  const handleTitleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      descriptionInputRef.current?.focus();
    }
  };

  // Scheduling handlers
  const handleNextWeekend = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysUntilSaturday = (6 - dayOfWeek) % 7;
    const nextSaturday = new Date(now);
    nextSaturday.setDate(now.getDate() + daysUntilSaturday + (daysUntilSaturday === 0 ? 7 : 0));
    
    const formattedDate = formatCalendarDate(nextSaturday);
    const nextSunday = new Date(nextSaturday);
    nextSunday.setDate(nextSaturday.getDate() + 1);
    const formattedEndDate = formatCalendarDate(nextSunday);
    
    setFormData(prev => ({ 
      ...prev, 
      startDate: formattedDate,
      endDate: isMultiDay ? formattedEndDate : prev.endDate
    }));
    setSchedulingMode('weekend');
  };

  const handleSomeday = () => {
    setFormData(prev => ({ ...prev, startDate: '', endDate: '' }));
    setSchedulingMode('someday');
  };

  const handlePickDate = () => {
    setShowCalendar(!showCalendar);
    if (!showCalendar) {
      setSchedulingMode('date');
    }
  };

  // Category selection handler
  const handleCategorySelect = (categoryValue) => {
    setFormData(prev => ({ ...prev, category: categoryValue }));
    
    // Update recent categories
    const recent = localStorage.getItem('recentCategories');
    let recentList = [];
    if (recent) {
      try {
        recentList = JSON.parse(recent);
      } catch {
        recentList = [];
      }
    }
    
    // Add to beginning, remove duplicates, keep only last 5
    recentList = [categoryValue, ...recentList.filter(cat => cat !== categoryValue)].slice(0, 5);
    localStorage.setItem('recentCategories', JSON.stringify(recentList));
    
    setShowExpandedCategories(false);
  };

  // Location selection handler
  const handleLocationSelect = (location) => {
    setFormData(prev => ({ ...prev, location }));
  };

  // Clear draft on successful submit
  const clearDraft = () => {
    localStorage.removeItem('familyEventDraft');
  };

  // Extract actual UUID from prefixed IDs and determine the source type
  const parseEventId = (id) => {
    if (typeof id === 'string' && id.includes('-')) {
      const parts = id.split('-');
      if (parts.length > 1) {
        const prefix = parts[0];
        const actualId = parts.slice(1).join('-');
        
        return {
          actualId,
          sourceType: prefix === 'activity' ? 'family_activity' : 
                     prefix === 'calendar' ? 'calendar_event' :
                     prefix === 'personal' ? 'calendar_event' : 'unknown',
          prefix
        };
      }
    }
    return { actualId: id, sourceType: 'unknown', prefix: null };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (!formData.title.trim()) {
        alert('Please enter an activity title');
        return;
      }

      // Clean up the form data to ensure proper null handling
      const cleanStartDate = formData.startDate && formData.startDate.trim() ? formData.startDate.trim() : null;
      const cleanEndDate = formData.endDate && formData.endDate.trim() ? formData.endDate.trim() : null;
      const cleanTime = formData.time && formData.time.trim() ? formData.time.trim() : null;
      const cleanLocation = formData.location && formData.location.trim() ? formData.location.trim() : null;

      const activityData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        start_date: cleanStartDate,
        end_date: cleanEndDate,
        // Legacy support: set date field for backward compatibility
        date: cleanStartDate,
        time: cleanTime || null, // Ensure null instead of empty string
        location: cleanLocation,
        confirmed_for_calendar: formData.confirmedForCalendar,
        image: formData.image,
        color_theme: formData.colorTheme,
        potential_dates: formData.potential_dates,
        status: cleanStartDate ? 'has_dates' : 'idea',
        // Enhanced details
        pricing: formData.priceRange || formData.priceNotes ? {
          range: formData.priceRange || null,
          notes: formData.priceNotes || null
        } : null,
        website: formData.website?.trim() || null,
        phone: formData.phone?.trim() || null,
        additional_images: formData.additionalImages?.length ? formData.additionalImages : null,
        estimated_duration: formData.estimatedDuration?.trim() || null,
        age_range: formData.ageRange?.trim() || null,
        special_requirements: formData.specialRequirements?.trim() || null,
        menu_highlights: formData.menuHighlights?.length ? formData.menuHighlights : null
      };

      // Set created_by from form data
      activityData.created_by = formData.created_by;

      console.log('📝 Submitting activity data:', activityData);

      let createdActivity;
      if (mode === 'edit' && event?.id) {
        // Parse the event ID to determine the correct update method
        const { actualId, sourceType, prefix } = parseEventId(event.id);
        console.log('📝 Editing event:', { originalId: event.id, actualId, sourceType, prefix });
        
        if (sourceType === 'calendar_event' || event.source === 'familyCalendar' || event.type === 'calendar-event') {
          // Update calendar event directly
          const { data, error } = await supabase
            .from('calendar_events')
            .update({
              title: activityData.title,
              description: activityData.description,
              date: activityData.date,
              time: activityData.time,
              location: activityData.location,
              category: activityData.category,
              image: activityData.image,
              created_by: formData.created_by
            })
            .eq('id', actualId)
            .select()
            .single();
          
          if (error) throw error;
          createdActivity = data;
        } else if (sourceType === 'family_activity' || prefix === 'activity') {
          // Update family activity (includes tentative/potential activities)
          createdActivity = await updateFamilyActivity(actualId, activityData);
        } else {
          // Fallback: try family activity first, then calendar event
          try {
            createdActivity = await updateFamilyActivity(actualId, activityData);
          } catch (familyError) {
            console.log('Family activity update failed, trying calendar event:', familyError);
            const { data, error } = await supabase
              .from('calendar_events')
              .update({
                title: activityData.title,
                description: activityData.description,
                date: activityData.date,
                time: activityData.time,
                location: activityData.location,
                category: activityData.category,
                image: activityData.image,
                created_by: formData.created_by
              })
              .eq('id', actualId)
              .select()
              .single();
            
            if (error) throw error;
            createdActivity = data;
          }
        }
      } else if (mode === 'add-calendar') {
        // Create calendar event directly
        const { data, error } = await supabase
          .from('calendar_events')
          .insert({
            title: activityData.title,
            description: activityData.description,
            date: activityData.date,
            time: activityData.time,
            location: activityData.location,
            category: activityData.category,
            image: activityData.image,
            type: 'custom',
            status: 'scheduled',
            created_by: formData.created_by,
            rsvp_enabled: formData.rsvpEnabled,
            rsvp_deadline: formData.rsvpEnabled && formData.rsvpDeadline ? formData.rsvpDeadline : null,
            rsvps: {}
          })
          .select()
          .single();
        
        if (error) throw error;
        createdActivity = data;
      } else {
        createdActivity = await addFamilyActivity(activityData);
      }
      
      // Note: We don't need to call addActivityToCalendar here anymore.
      // When creating a new family activity with confirmed_for_calendar=true,
      // the calendar entry should be created through a different flow
      // (e.g., when explicitly scheduling an unscheduled activity)
      
      clearDraft();
      onClose();
    } catch (err) {
      console.error('Error saving activity:', err);
      alert('Error saving activity: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setImageError('Please select a valid image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setImageError('Image file is too large (max 10MB)');
      return;
    }

    // Single-family architecture - no family check needed

    setImageError(null);
    setUploadingImage(true);
    
    try {
      console.log('📤 Uploading image to Supabase storage...');
      const result = await supabaseService.uploadImage(file, 'single-family');
      
      console.log('✅ Image uploaded successfully:', result.url);
      // Ensure we're storing just the URL string, not the whole object
      const imageUrl = typeof result === 'string' ? result : result.url;
      setFormData(prev => ({
        ...prev,
        image: imageUrl // Store the public URL string
      }));
    } catch (err) {
      console.error('❌ Error uploading image:', err);
      setImageError(err.message || 'Error uploading image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleImageUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            handleImageUpload(file);
            e.preventDefault();
            break;
          }
        }
      }
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: null }));
    setImageError(null);
  };

  const updatePotentialDate = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      potential_dates: prev.potential_dates.map(date =>
        date.id === id ? { 
          ...date, 
          [field]: field === 'date' ? (value.trim() || null) : value 
        } : date
      )
    }));
  };

  const removePotentialDate = (id) => {
    setFormData(prev => ({
      ...prev,
      potential_dates: prev.potential_dates.filter(date => date.id !== id)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-50 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-neutral-50 z-10">
          <h2 className="text-xl font-bold text-stone-900">
            {mode === 'edit' ? 'Edit Event' : mode === 'add-calendar' ? 'Add Calendar Event' : 'New Family Event'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-stone-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} onPaste={handlePaste} className="p-6 space-y-8">
          {/* Conversational Text Input */}
          <div className="space-y-4">
            <div>
              <input
                ref={titleInputRef}
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                onKeyPress={handleTitleKeyPress}
                onFocus={() => setTitleFocused(true)}
                onBlur={() => setTitleFocused(false)}
                className={`w-full px-6 py-4 text-lg border-2 rounded-xl transition-all duration-200 ${
                  titleFocused || formData.title 
                    ? 'border-gold-500 bg-coral-50/50' 
                    : 'border-stone-200 hover:border-stone-300'
                } focus:outline-none focus:ring-0`}
                placeholder={titleFocused ? "Let's go..." : "Let's go..."}
                autoComplete="off"
                required
              />
            </div>
            
            <div>
              <textarea
                ref={descriptionInputRef}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                onFocus={() => setDescriptionFocused(true)}
                onBlur={() => setDescriptionFocused(false)}
                rows={descriptionFocused || formData.description ? 4 : 3}
                className={`w-full px-6 py-4 text-base border-2 rounded-xl transition-all duration-200 resize-none ${
                  descriptionFocused || formData.description 
                    ? 'border-gold-500 bg-coral-50/50' 
                    : 'border-stone-200 hover:border-stone-300'
                } focus:outline-none focus:ring-0`}
                placeholder="Tell us more about it (optional)..."
                autoComplete="off"
              />
            </div>
          </div>

          {/* Smart Category Selection */}
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              {recentCategories.map(category => (
                <button
                  key={category.value}
                  type="button"
                  onClick={() => handleCategorySelect(category.value)}
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    formData.category === category.value
                      ? 'bg-gold-600 text-neutral-50 shadow-md'
                      : 'bg-action-subtle text-gold-700 hover:bg-gold-200'
                  }`}
                >
                  {category.icon}
                  <span className="ml-2">{category.label}</span>
                </button>
              ))}
              
              {!showExpandedCategories && (
                <button
                  type="button"
                  onClick={() => setShowExpandedCategories(true)}
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-stone-100 text-stone-700 hover:bg-stone-200 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  More
                </button>
              )}
            </div>

            {showExpandedCategories && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-4 bg-stone-50 rounded-xl">
                {categories.filter(cat => !recentCategories.some(recent => recent.value === cat.value)).map(category => (
                  <button
                    key={category.value}
                    type="button"
                    onClick={() => handleCategorySelect(category.value)}
                    className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      formData.category === category.value
                        ? 'bg-gold-600 text-neutral-50 shadow-md'
                        : 'bg-neutral-50 text-stone-700 hover:bg-gold-50 hover:text-gold-700 border border-stone-200'
                    }`}
                  >
                    {category.icon}
                    <span className="ml-2 truncate">{category.label}</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setShowExpandedCategories(false)}
                  className="col-span-full mt-2 px-3 py-2 text-sm text-stone-600 hover:text-stone-800 transition-colors"
                >
                  Show Less
                </button>
              </div>
            )}
          </div>

          {/* Smart Scheduling */}
          <div>
            <div className="mb-3">
              <span className="text-sm font-medium text-stone-700">When?</span>
            </div>
            
            <div className="flex flex-wrap gap-3 mb-4">
              <button
                type="button"
                onClick={handleNextWeekend}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  schedulingMode === 'weekend'
                    ? 'bg-gold-600 text-neutral-50 shadow-md'
                    : 'bg-action-subtle text-gold-700 hover:bg-gold-200'
                }`}
              >
                Next Weekend
              </button>
              
              <button
                type="button"
                onClick={handleSomeday}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  schedulingMode === 'someday'
                    ? 'bg-gold-600 text-neutral-50 shadow-md'
                    : 'bg-action-subtle text-gold-700 hover:bg-gold-200'
                }`}
              >
                Someday
              </button>
              
              <button
                type="button"
                onClick={handlePickDate}
                className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  schedulingMode === 'date' || showCalendar
                    ? 'bg-gold-600 text-neutral-50 shadow-md'
                    : 'bg-action-subtle text-gold-700 hover:bg-gold-200'
                }`}
              >
                <CalendarIcon className="w-4 h-4 mr-1" />
                {formData.startDate && schedulingMode === 'date' ? (
                  <>
                    {format(new Date(formData.startDate + 'T00:00:00'), 'MMM d')}
                    {formData.endDate && formData.endDate !== formData.startDate && (
                      <> - {format(new Date(formData.endDate + 'T00:00:00'), 'MMM d')}</>
                    )}
                    {formData.time && ` at ${formData.time}`}
                  </>
                ) : (
                  'Pick Date'
                )}
              </button>
            </div>

            {/* Toggleable Calendar */}
            {showCalendar && (
              <div className="bg-stone-900 text-neutral-50 rounded-xl p-4 transform transition-all duration-300 ease-in-out">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      type="button"
                      onClick={() => navigateCalendar(-1)}
                      className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <h3 className="text-lg font-medium">
                      {format(calendarDate, 'MMMM yyyy')}
                    </h3>
                    
                    <button
                      type="button"
                      onClick={() => navigateCalendar(1)}
                      className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1 text-center text-sm">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="p-2 text-stone-400 font-medium">
                        {day}
                      </div>
                    ))}
                    
                    {Array.from({ length: new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1).getDay() }, (_, i) => (
                      <div key={`empty-${i}`} className="p-2"></div>
                    ))}
                    
                    {Array.from({ length: new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 0).getDate() }, (_, i) => {
                      const day = i + 1;
                      const isToday = new Date().toDateString() === new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day).toDateString();
                      const currentDateFormatted = formatCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day));
                      const isStartDate = formData.startDate === currentDateFormatted;
                      const isEndDate = formData.endDate === currentDateFormatted;
                      const isInRange = formData.startDate && formData.endDate && 
                        currentDateFormatted >= formData.startDate && 
                        currentDateFormatted <= formData.endDate;
                      
                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => selectCalendarDate(day)}
                          className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                            isStartDate || isEndDate
                              ? 'bg-gold-600 text-neutral-50'
                              : isInRange
                                ? 'bg-gold-400 text-neutral-50'
                                : isToday
                                  ? 'bg-stone-700 text-neutral-50'
                                  : 'hover:bg-stone-800'
                          }`}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Multi-day and Time Controls */}
                <div className="pt-3 border-t border-stone-700 space-y-3">
                  {/* Multi-day toggle */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="multiDay"
                      checked={isMultiDay}
                      onChange={(e) => {
                        setIsMultiDay(e.target.checked);
                        if (!e.target.checked) {
                          // Clear end date when switching to single day
                          setFormData(prev => ({ ...prev, endDate: '' }));
                        } else if (formData.startDate && !formData.endDate) {
                          // Set end date to start date when enabling multi-day
                          setFormData(prev => ({ ...prev, endDate: prev.startDate }));
                        }
                      }}
                      className="w-4 h-4 text-gold-600 bg-stone-800 border-stone-600 rounded focus:ring-gold-500"
                    />
                    <label htmlFor="multiDay" className="text-sm text-stone-300">
                      Multi-day activity
                    </label>
                  </div>
                  
                  {/* End date input for multi-day */}
                  {isMultiDay && (
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-stone-400 min-w-[60px]">End date:</span>
                      <input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                        min={formData.startDate || undefined}
                        className="bg-stone-800 border border-stone-600 rounded-lg px-3 py-2 text-sm text-neutral-50 focus:outline-none focus:border-azure-400"
                      />
                    </div>
                  )}
                  
                  {/* Time input */}
                  <div className="flex items-center space-x-3">
                    <Clock className="w-4 h-4 text-stone-400" />
                    <input
                      type="time"
                      value={formData.time || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                      className="bg-stone-800 border border-stone-600 rounded-lg px-3 py-2 text-sm text-neutral-50 focus:outline-none focus:border-azure-400 placeholder-stone-500"
                      placeholder="Add time"
                    />
                    <span className="text-sm text-stone-400">Add Time (optional)</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Location Intelligence */}
          <div>
            <div className="mb-3">
              <span className="text-sm font-medium text-stone-700">Where?</span>
            </div>
            
            <div className="flex flex-wrap gap-2 mb-3">
              {quickLocations.map(location => (
                <button
                  key={location}
                  type="button"
                  onClick={() => handleLocationSelect(location)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    formData.location === location
                      ? 'bg-gold-600 text-neutral-50 shadow-md'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                >
                  {location}
                </button>
              ))}
            </div>
            
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                placeholder="Add location (optional)..."
              />
            </div>
          </div>

          {/* Calendar Confirmation */}
          {(formData.startDate || schedulingMode === 'date') && (
            <div className="bg-azure-50 border border-azure-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="confirmCalendar"
                  checked={formData.confirmedForCalendar}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmedForCalendar: e.target.checked }))}
                  className="w-4 h-4 text-azure-600 bg-white border-azure-300 rounded focus:ring-azure-500 mt-0.5"
                />
                <div className="flex-1">
                  <label htmlFor="confirmCalendar" className="text-sm font-medium text-azure-900 cursor-pointer">
                    Add to Family Calendar
                  </label>
                  <p className="text-xs text-azure-700 mt-1">
                    Check this to confirm the activity for your family calendar and open it for RSVPs. 
                    Leave unchecked if dates are tentative (like a festival that has specific dates but you haven't decided to attend yet).
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Smart Image Upload */}
          <div>
            <div className="mb-3">
              <span className="text-sm font-medium text-stone-700">Add a photo?</span>
            </div>
            
            {imageError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                {imageError}
              </div>
            )}

            {formData.image ? (
              <div className="relative group">
                <img 
                  src={formData.image} 
                  alt="Event preview" 
                  className="w-full h-48 object-cover rounded-xl border border-stone-200"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-3 right-3 bg-black bg-opacity-50 text-neutral-50 p-2 rounded-full hover:bg-opacity-75 transition-all opacity-0 group-hover:opacity-100"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer ${
                  uploadingImage 
                    ? 'border-gold-300 bg-coral-50/50 cursor-not-allowed opacity-75' 
                    : dragOver 
                      ? 'border-gold-500 bg-coral-50 scale-[1.02]' 
                      : 'border-stone-300 hover:border-stone-400 hover:bg-stone-50'
                }`}
                onClick={() => !uploadingImage && fileInputRef.current?.click()}
              >
                {uploadingImage ? (
                  <>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azure-600 mx-auto mb-3"></div>
                    <p className="text-sm text-gold-600 font-medium mb-1">
                      Uploading image...
                    </p>
                    <p className="text-xs text-gold-500">
                      Please wait while we save your image
                    </p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 mx-auto mb-3 bg-stone-100 rounded-full flex items-center justify-center">
                      <Upload className="w-6 h-6 text-stone-400" />
                    </div>
                    <p className="text-sm text-stone-700 font-medium mb-1">
                      Tap to add photo
                    </p>
                    <p className="text-xs text-stone-500">
                      Drop, paste, or click to upload • Up to 10MB
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploadingImage}
                />
              </div>
            )}
          </div>

          {/* Color Theme Selection */}
          {!formData.image && (
            <div>
              <div className="mb-3">
                <span className="text-sm font-medium text-stone-700">Choose a color theme</span>
                <p className="text-xs text-stone-500 mt-1">Pick a color that represents this activity</p>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {colorThemes.map((theme) => (
                  <button
                    key={theme.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, colorTheme: theme.value }))}
                    className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                      formData.colorTheme === theme.value
                        ? 'border-stone-800 ring-2 ring-stone-300'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    {/* Color preview with gradient */}
                    <div 
                      className={`w-full h-16 rounded-lg mb-3 ${theme.gradient} flex items-center justify-center`}
                    >
                      <span className="text-white text-sm font-semibold drop-shadow-sm">
                        {formData.title || 'Activity Title'}
                      </span>
                    </div>
                    
                    {/* Color name and description */}
                    <div className="text-left">
                      <div className="text-sm font-medium text-stone-900">{theme.label}</div>
                      <div className="text-xs text-stone-500 mt-1">{theme.description}</div>
                    </div>
                    
                    {/* Selection indicator */}
                    {formData.colorTheme === theme.value && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-stone-800 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Potential Dates (Optional, hidden by default in simplified flow) */}
          {formData.potential_dates.length > 0 && (
            <div>
              <div className="mb-3">
                <span className="text-sm font-medium text-stone-700">Potential Dates</span>
              </div>
              
              <div className="space-y-3 max-h-40 overflow-y-auto">
                {formData.potential_dates.map((dateOption, index) => (
                  <div key={dateOption.id} className="p-3 bg-stone-50 rounded-xl">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <input
                        type="date"
                        value={dateOption.date}
                        onChange={(e) => updatePotentialDate(dateOption.id, 'date', e.target.value)}
                        className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                      />
                      <select
                        value={dateOption.timeRange}
                        onChange={(e) => updatePotentialDate(dateOption.id, 'timeRange', e.target.value)}
                        className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                      >
                        <option value="All day">All day</option>
                                        <option value="Intention 1">Intention 1</option>
                <option value="Intention 2">Intention 2</option>
                <option value="Intention 3">Intention 3</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between">
                      <input
                        type="text"
                        value={dateOption.label}
                        onChange={(e) => updatePotentialDate(dateOption.id, 'label', e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                      />
                      <button
                        type="button"
                        onClick={() => removePotentialDate(dateOption.id)}
                        className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Enhanced Details Section */}
          <div className="space-y-6 bg-stone-50 -mx-6 px-6 py-6 rounded-xl">
            <div className="text-sm font-medium text-stone-700 mb-4">
              Additional Details (Optional)
            </div>
            
            {/* Pricing Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Price Range
                </label>
                <input
                  type="text"
                  value={formData.priceRange}
                  onChange={(e) => setFormData(prev => ({ ...prev, priceRange: e.target.value }))}
                  placeholder="e.g., $15-25 per person, Free, $$"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Duration
                </label>
                <input
                  type="text"
                  value={formData.estimatedDuration}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimatedDuration: e.target.value }))}
                  placeholder="e.g., 2 hours, All day, 30-45 minutes"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                />
              </div>
            </div>

            {/* Age Range and Requirements */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  <Users className="w-4 h-4 inline mr-1" />
                  Age Range
                </label>
                <input
                  type="text"
                  value={formData.ageRange}
                  onChange={(e) => setFormData(prev => ({ ...prev, ageRange: e.target.value }))}
                  placeholder="e.g., All ages, 5+, Adults only"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Special Requirements
                </label>
                <input
                  type="text"
                  value={formData.specialRequirements}
                  onChange={(e) => setFormData(prev => ({ ...prev, specialRequirements: e.target.value }))}
                  placeholder="e.g., Reservations required, Bring sunscreen"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                />
              </div>
            </div>

            {/* Pricing Notes */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                <Menu className="w-4 h-4 inline mr-1" />
                Pricing Notes / Menu Highlights
              </label>
              <textarea
                value={formData.priceNotes}
                onChange={(e) => setFormData(prev => ({ ...prev, priceNotes: e.target.value }))}
                placeholder="e.g., Kids eat free on Sundays, Happy hour 3-6pm, Parking is $5"
                rows={3}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-azure-500 focus:border-azure-500 resize-none"
              />
            </div>
          </div>

          {/* Auto-save indicator */}
          {(formData.title.trim() || formData.description.trim()) && (
            <div className="flex items-center justify-center py-2">
              <div className="flex items-center text-xs text-stone-500">
                <div className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></div>
                Draft saved
              </div>
            </div>
          )}

          {/* Creator Selection */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Created by
            </label>
            <select
              value={formData.created_by}
              onChange={(e) => setFormData(prev => ({ ...prev, created_by: e.target.value }))}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
            >
              {allFamilyMembers.map(member => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>

          {/* Enhanced Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting || uploadingImage || !formData.title.trim()}
              className="w-full inline-flex items-center justify-center px-8 py-4 bg-indigo-800 text-neutral-50 font-semibold text-lg rounded-xl hover:bg-emerald-700 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
            >
              {uploadingImage ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-neutral-50 mr-3"></div>
                  Uploading Image...
                </>
              ) : isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-neutral-50 mr-3"></div>
                  Creating Event...
                </>
              ) : (
                <>
                  <span className="mr-2">▶</span>
                  {mode === 'edit' ? 'Save Changes' : 'Create Event'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}